import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  api,
  formatApiErrorDetail,
} from "@/lib/api";

import {
  useFarm,
} from "@/context/FarmContext";

import {
  formatCurrency,
} from "@/lib/currency";

import {
  PageHeader,
  NoFarm,
} from "@/components/Page";

import {
  Card,
} from "@/components/ui/card";

import {
  Button,
} from "@/components/ui/button";

import {
  Input,
} from "@/components/ui/input";

import {
  Checkbox,
} from "@/components/ui/checkbox";

import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import {
  toast,
} from "sonner";

import {
  AlertTriangle,
  Archive,
  ArrowRight,
  BookOpen,
  Calculator,
  CheckCircle2,
  ChevronDown,
  Copy,
  Eye,
  GitCompare,
  Pencil,
  RotateCcw,
  Save,
  Scale,
  Sparkles,
  Star,
  Wheat,
  XCircle,
} from "lucide-react";


const ratioPct = (value) =>
  value == null
    ? "—"
    : `${Math.round(Number(value))}%`;


const fmtDate = (value) => {
  if (!value) {
    return "—";
  }

  const date =
    new Date(
      value
    );

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "—";
  }

  return date.toLocaleDateString(
    "en-IE",
    {
      day: "numeric",
      month: "short",
      year: "numeric",
    }
  );
};


const numberText = (
  value,
  decimals = 1
) => {
  const parsed =
    Number(
      value
    );

  if (
    !Number.isFinite(
      parsed
    )
  ) {
    return "—";
  }

  return parsed.toLocaleString(
    "en-IE",
    {
      minimumFractionDigits:
        decimals,
      maximumFractionDigits:
        decimals,
    }
  );
};


// ---------------------------------------------------------
// Existing result interpretation — no new equations
// ---------------------------------------------------------

function pctStatus(
  pct,
  {
    lowDef = 90,
    lowRev = 95,
    highRev = 120,
  } = {}
) {
  if (pct == null) {
    return "review";
  }

  if (pct < lowDef) {
    return "deficient";
  }

  if (
    pct < lowRev ||
    pct > highRev
  ) {
    return "review";
  }

  return "adequate";
}


function ndfStatus(
  ndf
) {
  if (!ndf) {
    return "review";
  }

  if (ndf < 28) {
    return "deficient";
  }

  if (
    ndf < 32 ||
    ndf > 50
  ) {
    return "review";
  }

  return "adequate";
}


function starchStatus(
  starch
) {
  if (starch == null) {
    return "adequate";
  }

  if (starch > 22) {
    return "review";
  }

  return "adequate";
}


const worst = (
  statuses
) =>
  statuses.includes(
    "deficient"
  )
    ? "deficient"
    : statuses.includes(
        "review"
      )
    ? "review"
    : "adequate";


const RESULT_CFG = {
  adequate: {
    label:
      "Adequate",

    cls:
      "bg-primary/[0.06] text-primary border-primary/30",

    strongCls:
      "bg-primary text-primary-foreground",

    Icon:
      CheckCircle2,
  },

  review: {
    label:
      "Review",

    cls:
      "bg-[hsl(43,74%,52%)]/10 text-[hsl(38,70%,30%)] border-[hsl(43,74%,52%)]/40",

    strongCls:
      "bg-[hsl(43,74%,52%)] text-white",

    Icon:
      AlertTriangle,
  },

  deficient: {
    label:
      "Deficient",

    cls:
      "bg-destructive/[0.06] text-destructive border-destructive/35",

    strongCls:
      "bg-destructive text-destructive-foreground",

    Icon:
      XCircle,
  },
};


function StatusPill({
  status,
}) {
  const config =
    RESULT_CFG[
      status
    ] ||
    RESULT_CFG.review;

  const Icon =
    config.Icon;

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] ${config.cls}`}
    >
      <Icon className="h-3 w-3" />
      {config.label}
    </span>
  );
}


function SummaryTile({
  label,
  value,
  unit,
  status,
  hint,
}) {
  return (
    <div className="rounded-xl border bg-background p-4">

      <div className="flex items-start justify-between gap-2">

        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          {label}
        </p>

        {status && (
          <StatusPill
            status={
              status
            }
          />
        )}

      </div>

      <p className="mt-2 font-display text-2xl font-extrabold tabular-nums text-[hsl(148,38%,16%)]">

        {value}

        {unit && (
          <span className="ml-1 text-xs font-semibold text-muted-foreground">
            {unit}
          </span>
        )}

      </p>

      {hint && (
        <p className="mt-1 text-xs text-muted-foreground">
          {hint}
        </p>
      )}

    </div>
  );
}


function BalanceRow({
  label,
  unit,
  data,
}) {
  if (!data) {
    return null;
  }

  const deficient =
    data.status ===
    "deficit";

  const review =
    data.status ===
    "review";

  const statusClass =
    deficient
      ? "bg-destructive/10 text-destructive"
      : review
      ? "bg-[hsl(43,74%,52%)]/15 text-[hsl(38,70%,30%)]"
      : "bg-primary/10 text-primary";

  return (
    <TableRow>

      <TableCell className="font-medium">

        {label}

        <span className="ml-1 text-xs text-muted-foreground">
          {unit}
        </span>

      </TableCell>

      <TableCell className="text-right tabular-nums">
        {numberText(
          data.requirement
        )}
      </TableCell>

      <TableCell className="text-right tabular-nums">
        {numberText(
          data.supply
        )}
      </TableCell>

      <TableCell
        className={`text-right tabular-nums font-semibold ${
          deficient
            ? "text-destructive"
            : review
            ? "text-[hsl(38,70%,35%)]"
            : "text-primary"
        }`}
      >
        {Number(
          data.balance
        ) > 0
          ? "+"
          : ""}
        {numberText(
          data.balance
        )}
      </TableCell>

      <TableCell className="text-right">

        <span
          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${statusClass}`}
        >
          {data.status} ·{" "}
          {data.pct ??
            "—"}%
        </span>

      </TableCell>

    </TableRow>
  );
}


const MACRO_MINERALS = [
  [
    "Calcium",
    "ca",
    "g/day",
  ],
  [
    "Phosphorus",
    "p",
    "g/day",
  ],
  [
    "Magnesium",
    "mg",
    "g/day",
  ],
  [
    "Potassium",
    "k",
    "g/day",
  ],
  [
    "Sodium",
    "na",
    "g/day",
  ],
  [
    "Sulphur",
    "s",
    "g/day",
  ],
  [
    "Chloride",
    "cl",
    "g/day",
  ],
];


const TRACE_MINERALS = [
  [
    "Copper",
    "cu",
    "mg/day",
  ],
  [
    "Zinc",
    "zn",
    "mg/day",
  ],
  [
    "Manganese",
    "mn",
    "mg/day",
  ],
  [
    "Selenium",
    "se",
    "mg/day",
  ],
  [
    "Iodine",
    "i",
    "mg/day",
  ],
  [
    "Cobalt",
    "co",
    "mg/day",
  ],
  [
    "Iron",
    "fe",
    "mg/day",
  ],
  [
    "Molybdenum",
    "mo",
    "mg/day",
  ],
];


function MineralTable({
  title,
  rows,
  balances,
}) {
  return (
    <Card className="overflow-x-auto">

      <div className="border-b border-border p-4">

        <h3 className="font-display font-bold text-[hsl(148,38%,16%)]">
          {title}
        </h3>

      </div>

      <Table>

        <TableHeader>

          <TableRow>

            <TableHead>
              Nutrient
            </TableHead>

            <TableHead className="text-right">
              Requirement
            </TableHead>

            <TableHead className="text-right">
              Supply
            </TableHead>

            <TableHead className="text-right">
              Balance
            </TableHead>

            <TableHead className="text-right">
              Status
            </TableHead>

          </TableRow>

        </TableHeader>

        <TableBody>

          {rows.map(
            ([
              label,
              key,
              unit,
            ]) => (
              <BalanceRow
                key={
                  key
                }
                label={
                  label
                }
                unit={
                  unit
                }
                data={
                  balances?.[
                    key
                  ]
                }
              />
            )
          )}

        </TableBody>

      </Table>

    </Card>
  );
}


function DcadPanel({
  dcad,
}) {
  if (!dcad) {
    return null;
  }

  const status =
    dcad.status ===
    "adequate"
      ? "adequate"
      : "review";

  const config =
    RESULT_CFG[
      status
    ];

  return (
    <Card
      className={`border-l-4 p-5 ${config.cls}`}
      data-testid="dcad-panel"
    >

      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">

        <div>

          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] opacity-70">
            Dietary cation-anion difference
          </p>

          <p className="mt-1 font-display text-4xl font-extrabold">

            {numberText(
              dcad.value,
              1
            )}

            <span className="ml-2 text-sm font-semibold opacity-70">
              {dcad.unit}
            </span>

          </p>

          <p className="mt-2 text-xs opacity-75">
            Target{" "}
            {dcad.target_min}–
            {dcad.target_max}{" "}
            {dcad.unit} ·{" "}
            {dcad.context}
          </p>

        </div>

        <StatusPill
          status={
            status
          }
        />

      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4">

        {[
          [
            "Na",
            dcad.components
              ?.na_pct_dm,
          ],
          [
            "K",
            dcad.components
              ?.k_pct_dm,
          ],
          [
            "Cl",
            dcad.components
              ?.cl_pct_dm,
          ],
          [
            "S",
            dcad.components
              ?.s_pct_dm,
          ],
        ].map(
          ([
            label,
            value,
          ]) => (
            <div
              key={
                label
              }
              className="rounded-lg border bg-white/50 p-3"
            >

              <p className="text-[10px] uppercase tracking-wider opacity-70">
                {label}
              </p>

              <p className="font-display text-xl font-bold">
                {numberText(
                  value,
                  3
                )}
                %
              </p>

            </div>
          )
        )}

      </div>

      <p className="mt-4 text-xs opacity-75">
        DCAD = Na% × 434.98 + K% × 255.75 − Cl% × 282.09 − S% × 623.75.
      </p>

    </Card>
  );
}


export default function NutritionEvaluation() {
  const {
    farmId,
    farm,
    loading,
  } =
    useFarm();

  const currency =
    farm?.currency ||
    "EUR";

  const qc =
    useQueryClient();

  const [
    groupId,
    setGroupId,
  ] =
    useState(
      ""
    );

  const [
    sel,
    setSel,
  ] =
    useState(
      {}
    );

  const [
    name,
    setName,
  ] =
    useState(
      "New diet"
    );

  const [
    lineageId,
    setLineageId,
  ] =
    useState(
      null
    );

  const [
    loadedId,
    setLoadedId,
  ] =
    useState(
      null
    );

  const [
    dirty,
    setDirty,
  ] =
    useState(
      false
    );

  const [
    view,
    setView,
  ] =
    useState(
      "simple"
    );

  const [
    managementTab,
    setManagementTab,
  ] =
    useState(
      "saved"
    );

  const [
    result,
    setResult,
  ] =
    useState(
      null
    );

  const [
    assignTarget,
    setAssignTarget,
  ] =
    useState(
      null
    );

  const [
    saveActiveOpen,
    setSaveActiveOpen,
  ] =
    useState(
      false
    );

  const [
    compareId,
    setCompareId,
  ] =
    useState(
      null
    );

  const [
    showArchived,
    setShowArchived,
  ] =
    useState(
      false
    );

  const [
    search,
    setSearch,
  ] =
    useState(
      ""
    );

  const [
    showAllRecommendations,
    setShowAllRecommendations,
  ] =
    useState(
      false
    );

  const skipDirty =
    useRef(
      false
    );


  const {
    data:
      groups = [],
  } =
    useQuery({
      queryKey: [
        "animal-groups",
        farmId,
      ],

      queryFn:
        async () =>
          (
            await api.get(
              `/farms/${farmId}/animal-groups`
            )
          ).data,

      enabled:
        Boolean(
          farmId
        ),
    });


  const {
    data:
      feeds = [],
  } =
    useQuery({
      queryKey: [
        "feeds",
        farmId,
      ],

      queryFn:
        async () =>
          (
            await api.get(
              `/farms/${farmId}/feeds`
            )
          ).data,

      enabled:
        Boolean(
          farmId
        ),
    });


  const {
    data:
      rations = [],
  } =
    useQuery({
      queryKey: [
        "rations",
        farmId,
        showArchived,
      ],

      queryFn:
        async () =>
          (
            await api.get(
              `/farms/${farmId}/rations?include_archived=${showArchived}`
            )
          ).data,

      enabled:
        Boolean(
          farmId
        ),
    });


  const group =
    useMemo(
      () =>
        groups.find(
          (
            item
          ) =>
            item.id ===
            groupId
        ),
      [
        groups,
        groupId,
      ]
    );


  const activeRation =
    useMemo(
      () =>
        rations.find(
          (
            ration
          ) =>
            ration.id ===
            group
              ?.active_ration_id
        ),
      [
        rations,
        group,
      ]
    );


  const activeRations =
    useMemo(
      () =>
        rations.filter(
          (
            ration
          ) =>
            !ration.archived
        ),
      [
        rations,
      ]
    );


  const compareRation =
    useMemo(
      () =>
        rations.find(
          (
            ration
          ) =>
            ration.id ===
            compareId
        ),
      [
        rations,
        compareId,
      ]
    );


  const filteredFeeds =
    useMemo(
      () => {
        const query =
          search
            .trim()
            .toLowerCase();

        if (!query) {
          return feeds;
        }

        return feeds.filter(
          (
            feed
          ) =>
            (
              feed.name ||
              ""
            )
              .toLowerCase()
              .includes(
                query
              ) ||
            (
              feed.category ||
              ""
            )
              .toLowerCase()
              .includes(
                query
              )
        );
      },
      [
        feeds,
        search,
      ]
    );


  useEffect(
    () => {
      if (
        !groupId &&
        groups.length
      ) {
        setGroupId(
          groups[0].id
        );
      }
    },
    [
      groups,
      groupId,
    ]
  );


  const loadItems =
    useCallback(
      (
        ration
      ) => {
        skipDirty.current =
          true;

        const next =
          {};

        (
          ration?.items ||
          []
        ).forEach(
          (
            item
          ) => {
            next[
              item.feed_id
            ] =
              item.inclusion_kg_dm;
          }
        );

        setSel(
          next
        );

        setName(
          ration?.name ||
            "New diet"
        );

        setLineageId(
          ration
            ?.lineage_id ||
            null
        );

        setLoadedId(
          ration?.id ||
            null
        );

        setDirty(
          false
        );
      },
      []
    );


  useEffect(
    () => {
      if (!group) {
        return;
      }

      const active =
        rations.find(
          (
            ration
          ) =>
            ration.id ===
            group
              .active_ration_id
        );

      loadItems(
        active ||
          null
      );

      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [
      groupId,
      group
        ?.active_ration_id,
      rations.length,
    ]
  );


  const items =
    useMemo(
      () =>
        Object.entries(
          sel
        )
          .filter(
            (
              [
                ,
                value,
              ]
            ) =>
              Number(
                value
              ) > 0
          )
          .map(
            (
              [
                feed_id,
                value,
              ]
            ) => ({
              feed_id,

              inclusion_kg_dm:
                Number(
                  value
                ),
            })
          ),
      [
        sel,
      ]
    );


  useEffect(
    () => {
      if (
        !farmId ||
        !groupId
      ) {
        return;
      }

      const timer =
        setTimeout(
          async () => {
            try {
              const {
                data,
              } =
                await api.post(
                  `/farms/${farmId}/rations/evaluate/${groupId}`,
                  {
                    items,
                  }
                );

              setResult(
                data
              );

            } catch {
              setResult(
                null
              );
            }
          },
          350
        );

      return () =>
        clearTimeout(
          timer
        );
    },
    [
      farmId,
      groupId,
      items,
    ]
  );


  const setKg = (
    id,
    value
  ) => {
    setSel(
      (
        current
      ) => ({
        ...current,
        [id]:
          value,
      })
    );

    if (
      skipDirty.current
    ) {
      skipDirty.current =
        false;

    } else {
      setDirty(
        true
      );
    }
  };


  const toggle = (
    id,
    on
  ) => {
    setSel(
      (
        current
      ) => {
        const next = {
          ...current,
        };

        if (on) {
          next[id] =
            next[id] ||
            1;

        } else {
          delete next[
            id
          ];
        }

        return next;
      }
    );

    setDirty(
      true
    );
  };


  const refreshAll =
    () => {
      qc.invalidateQueries({
        queryKey: [
          "rations",
          farmId,
        ],
      });

      qc.invalidateQueries({
        queryKey: [
          "animal-groups",
          farmId,
        ],
      });

      qc.invalidateQueries({
        queryKey: [
          "dashboard",
          farmId,
        ],
      });

      qc.invalidateQueries({
        queryKey: [
          "advisor",
          farmId,
        ],
      });

      qc.invalidateQueries({
        queryKey: [
          "advisor-summary",
          farmId,
        ],
      });
    };


  const saveVersion =
    async (
      assign
    ) => {
      if (
        !items.length
      ) {
        toast.error(
          "Add at least one feed"
        );

        return;
      }

      try {
        await api.post(
          `/farms/${farmId}/rations`,
          {
            name,
            items,
            lineage_id:
              lineageId,
            group_id:
              assign
                ? groupId
                : null,
          }
        );

        toast.success(
          assign
            ? "Saved as new version & set active"
            : "Saved as new immutable version"
        );

        setDirty(
          false
        );

        refreshAll();

      } catch (
        error
      ) {
        toast.error(
          formatApiErrorDetail(
            error.response
              ?.data
              ?.detail
          )
        );
      }
    };


  const doAction =
    async (
      fn,
      message
    ) => {
      try {
        await fn();

        toast.success(
          message
        );

        refreshAll();

      } catch (
        error
      ) {
        toast.error(
          formatApiErrorDetail(
            error.response
              ?.data
              ?.detail
          )
        );
      }
    };


  const duplicate = (
    ration
  ) =>
    doAction(
      () =>
        api.post(
          `/farms/${farmId}/rations/${ration.id}/duplicate`,
          {}
        ),
      "Duplicated as a new diet"
    );


  const rename = (
    ration
  ) => {
    const nextName =
      window.prompt(
        "Rename diet",
        ration.name
      );

    if (
      nextName &&
      nextName !==
        ration.name
    ) {
      doAction(
        () =>
          api.patch(
            `/farms/${farmId}/rations/${ration.id}`,
            {
              name:
                nextName,
            }
          ),
        "Renamed (history unchanged)"
      );
    }
  };


  const archive = (
    ration
  ) =>
    doAction(
      () =>
        api.patch(
          `/farms/${farmId}/rations/${ration.id}`,
          {
            archived:
              !ration.archived,
          }
        ),
      ration.archived
        ? "Unarchived"
        : "Archived (hidden, not deleted)"
    );


  const restore = (
    ration
  ) =>
    doAction(
      async () => {
        await api.post(
          `/farms/${farmId}/rations/${ration.id}/restore`,
          {}
        );
      },
      "Restored as a new version"
    );


  const confirmAssign =
    async () => {
      const id =
        assignTarget;

      setAssignTarget(
        null
      );

      await doAction(
        () =>
          api.post(
            `/farms/${farmId}/rations/${id}/assign`,
            {
              group_id:
                groupId,
            }
          ),
        "Active diet changed"
      );
    };


  if (
    !loading &&
    !farmId
  ) {
    return (
      <NoFarm />
    );
  }


  const balance =
    result?.balance;

  const ration =
    result?.ration;


  const eStatus =
    pctStatus(
      balance
        ?.balances
        ?.energy_nasem
        ?.pct
    );

  const pStatus =
    pctStatus(
      balance
        ?.balances
        ?.mp
        ?.pct
    );

  const fStatus =
    ndfStatus(
      ration?.ndf_pct
    );

  const stStatus =
    starchStatus(
      ration?.starch_pct
    );


  const mineralStatuses =
    Object.values(
      balance
        ?.balances
        ?.minerals ||
        {}
    ).map(
      (
        value
      ) =>
        value?.status ===
        "deficit"
          ? "deficient"
          : value?.status ===
            "review"
          ? "review"
          : "adequate"
    );


  const dcadStatus =
    balance?.dcad
      ?.status ===
    "adequate"
      ? "adequate"
      : "review";


  const overall =
    worst([
      eStatus,
      pStatus,
      fStatus,
      dcadStatus,
      ...mineralStatuses,
    ]);


  const overallReason =
    overall === "deficient"
      ? (
          mineralStatuses.includes("deficient")
            ? "Mineral balance"
            : eStatus === "deficient"
            ? "Energy"
            : pStatus === "deficient"
            ? "Protein"
            : fStatus === "deficient"
            ? "Fibre"
            : "Nutrition balance"
        )
      : overall === "review"
      ? (
          mineralStatuses.includes("review")
            ? "Mineral balance"
            : dcadStatus === "review"
            ? "DCAD"
            : stStatus === "review"
            ? "Starch"
            : "Nutrition balance"
        )
      : "Well balanced";


  const mineralProblem =
    mineralStatuses.some(
      (
        status
      ) =>
        status !==
        "adequate"
    );


  const simpleRecs =
    [];

  if (balance) {
    if (
      eStatus ===
      "deficient"
    ) {
      simpleRecs.push(
        "Energy is short — add a high-energy feed or improve forage quality."
      );

    } else if (
      balance
        .balances
        .energy_nasem
        .pct >
      120
    ) {
      simpleRecs.push(
        "Energy is over-supplied — there may be scope to reduce concentrate cost."
      );
    }

    if (
      pStatus ===
      "deficient"
    ) {
      simpleRecs.push(
        "Protein is short — review protein sources or forage quality."
      );
    }

    if (
      fStatus ===
      "deficient"
    ) {
      simpleRecs.push(
        "Fibre is low — increase forage or effective fibre to support rumen function."
      );
    }

    if (
      ration
        ?.starch_pct >
      28
    ) {
      simpleRecs.push(
        "Starch is high — review acidosis risk and effective fibre."
      );
    }

    const mineralNames = {
      ca:
        "Calcium",
      p:
        "Phosphorus",
      mg:
        "Magnesium",
      k:
        "Potassium",
      na:
        "Sodium",
      s:
        "Sulphur",
      cl:
        "Chloride",
      cu:
        "Copper",
      zn:
        "Zinc",
      mn:
        "Manganese",
      se:
        "Selenium",
      i:
        "Iodine",
      co:
        "Cobalt",
      fe:
        "Iron",
      mo:
        "Molybdenum",
    };

    Object.entries(
      balance
        .balances
        ?.minerals ||
        {}
    ).forEach(
      ([
        key,
        value,
      ]) => {
        if (
          value?.status ===
          "deficit"
        ) {
          simpleRecs.push(
            `${
              mineralNames[
                key
              ] ||
              key
            } is below the screening target. Review the mineral specification before changing the ration.`
          );
        }
      }
    );

    if (
      balance.dcad
        ?.status ===
      "low"
    ) {
      simpleRecs.push(
        `DCAD is low at ${balance.dcad.value} mEq/kg DM. Review sodium, potassium, chloride and sulphur sources.`
      );
    }

    if (
      balance.dcad
        ?.status ===
      "high"
    ) {
      simpleRecs.push(
        `DCAD is above the lactating screening range at ${balance.dcad.value} mEq/kg DM. Review the intended production stage and mineral strategy.`
      );
    }

    if (
      !simpleRecs.length
    ) {
      simpleRecs.push(
        "This ration looks well balanced for the selected animal group."
      );
    }
  }


  const selectedFeedCount =
    items.length;


  const loadedRation =
    loadedId
      ? rations.find(
          (
            rationItem
          ) =>
            rationItem.id ===
            loadedId
        )
      : null;


  const activeDmi =
    activeRation
      ?.result
      ?.total_dmi ??
    activeRation
      ?.result
      ?.ration
      ?.total_dmi ??
    null;


  const activeCost =
    activeRation
      ?.result
      ?.cost_per_cow_day ??
    activeRation
      ?.result
      ?.ration
      ?.cost_per_cow_day ??
    null;


  return (
    <div>

      <PageHeader
        title="Rations & Nutrition"
        subtitle="Build, evaluate and manage feeding plans for each animal group."
        action={
          <div
            className="inline-flex rounded-full border border-border p-0.5"
            data-testid="view-toggle"
          >

            <button
              data-testid="view-simple"
              onClick={() =>
                setView(
                  "simple"
                )
              }
              className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
                view ===
                "simple"
                  ? "bg-primary text-white"
                  : "text-muted-foreground"
              }`}
            >
              Simple
            </button>

            <button
              data-testid="view-professional"
              onClick={() =>
                setView(
                  "professional"
                )
              }
              className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
                view ===
                "professional"
                  ? "bg-primary text-white"
                  : "text-muted-foreground"
              }`}
            >
              Professional
            </button>

          </div>
        }
      />


      {/* -------------------------------------------------- */}
      {/* Group selector + active ration                     */}
      {/* -------------------------------------------------- */}

      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">

        <div className="w-full lg:w-72">

          <p className="mb-1.5 text-xs font-semibold text-muted-foreground">
            Animal group
          </p>

          <Select
            value={
              groupId
            }
            onValueChange={(
              value
            ) => {
              if (
                dirty &&
                !window.confirm(
                  "You have unsaved changes. Switch group and discard them?"
                )
              ) {
                return;
              }

              setGroupId(
                value
              );
            }}
          >

            <SelectTrigger data-testid="group-select">
              <SelectValue placeholder="Select group" />
            </SelectTrigger>

            <SelectContent>

              {groups.map(
                (
                  item
                ) => (
                  <SelectItem
                    key={
                      item.id
                    }
                    value={
                      item.id
                    }
                  >
                    {item.name}
                  </SelectItem>
                )
              )}

            </SelectContent>

          </Select>

        </div>

        {loadedRation && (
          <p className="text-xs text-muted-foreground">
            Editing:{" "}
            <strong className="text-foreground">
              {loadedRation.name} v
              {loadedRation.version ??
                1}
            </strong>
          </p>
        )}

      </div>


      <Card
        className="mb-6 border-primary/20 bg-primary/[0.035] p-5"
        data-testid="active-diet-banner"
      >

        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">

          <div className="flex items-start gap-3">

            <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-primary/10">
              <Star className="h-5 w-5 text-primary" />
            </div>

            <div>

              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-primary">
                Active ration
              </p>

              <div className="mt-1 flex flex-wrap items-center gap-2">

                <h2
                  className="font-display text-xl font-bold text-[hsl(148,38%,16%)]"
                  data-testid="active-diet-name"
                >
                  {activeRation
                    ? `${activeRation.name} · v${activeRation.version ?? 1}`
                    : "No active ration assigned"}
                </h2>

                {activeRation && (
                  <span className="rounded-full bg-background px-2.5 py-1 text-[10px] font-semibold text-muted-foreground">
                    Active since{" "}
                    {fmtDate(
                      activeRation.created_at
                    )}
                  </span>
                )}

              </div>

              <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground">

                {activeDmi != null && (
                  <span>
                    <strong className="text-foreground">
                      {numberText(
                        activeDmi,
                        1
                      )} kg
                    </strong>{" "}
                    DM/head/day
                  </span>
                )}

                {activeCost != null && (
                  <span>
                    <strong className="text-foreground">
                      {formatCurrency(
                        activeCost,
                        currency,
                        2
                      )}
                    </strong>{" "}
                    /head/day
                  </span>
                )}

              </div>

            </div>

          </div>

          {activeRation && (
            <Button
              variant="outline"
              className="rounded-full"
              data-testid="active-load-btn"
              onClick={() =>
                loadItems(
                  activeRation
                )
              }
            >
              <Eye className="mr-2 h-4 w-4" />
              Load active ration
            </Button>
          )}

        </div>

      </Card>


      {/* -------------------------------------------------- */}
      {/* Main workspace                                    */}
      {/* -------------------------------------------------- */}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">

        {/* ------------------------------------------------ */}
        {/* Builder                                         */}
        {/* ------------------------------------------------ */}

        <Card className="h-fit p-5 xl:col-span-2">

          <div className="mb-5 flex items-start justify-between gap-3">

            <div className="flex items-center gap-3">

              <div className="grid h-10 w-10 place-items-center rounded-full bg-primary/10">
                <Calculator className="h-5 w-5 text-primary" />
              </div>

              <div>

                <h3 className="font-display text-lg font-bold text-[hsl(148,38%,16%)]">
                  Build ration
                </h3>

                <p className="mt-0.5 text-xs text-muted-foreground">
                  Enter kg DM/head/day for the selected group.
                </p>

              </div>

            </div>

            {dirty && (
              <span
                className="rounded-full bg-[hsl(43,74%,52%)]/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-[hsl(38,70%,30%)]"
                data-testid="unsaved-badge"
              >
                Unsaved
              </span>
            )}

          </div>


          <div className="mb-3">

            <Input
              value={
                search
              }
              onChange={(
                event
              ) =>
                setSearch(
                  event.target
                    .value
                )
              }
              placeholder="Search feeds..."
            />

          </div>


          {feeds.length ===
            0 && (
            <div className="rounded-xl border border-dashed p-8 text-center">

              <Wheat className="mx-auto h-6 w-6 text-muted-foreground" />

              <p className="mt-2 text-sm font-semibold">
                No feeds available
              </p>

              <p className="mt-1 text-xs text-muted-foreground">
                Add feeds in Feed Inventory first.
              </p>

            </div>
          )}


          <div className="max-h-[350px] space-y-2 overflow-y-auto pr-1">

            {filteredFeeds.map(
              (
                feed
              ) => {
                const on =
                  sel[
                    feed.id
                  ] !=
                  null;

                return (
                  <div
                    key={
                      feed.id
                    }
                    data-testid={`eval-feed-${feed.id}`}
                    className={`rounded-xl border p-3 transition-colors ${
                      on
                        ? "border-primary/40 bg-primary/[0.035]"
                        : "border-border bg-background"
                    }`}
                  >

                    <div className="flex items-center gap-3">

                      <Checkbox
                        checked={
                          on
                        }
                        data-testid={`eval-check-${feed.id}`}
                        onCheckedChange={(
                          checked
                        ) =>
                          toggle(
                            feed.id,
                            checked
                          )
                        }
                      />

                      <div className="min-w-0 flex-1">

                        <p className="truncate text-sm font-semibold text-[hsl(148,38%,16%)]">
                          {feed.name}
                        </p>

                        <p className="mt-0.5 text-[10px] text-muted-foreground">
                          {feed.category ||
                            "Feed"}
                          {" · "}
                          {numberText(
                            feed.dm_pct,
                            1
                          )}
                          % DM
                        </p>

                      </div>

                      <div className="flex items-center gap-2">

                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          disabled={
                            !on
                          }
                          data-testid={`eval-input-${feed.id}`}
                          value={
                            sel[
                              feed.id
                            ] ??
                            ""
                          }
                          onChange={(
                            event
                          ) =>
                            setKg(
                              feed.id,
                              event.target
                                .value
                            )
                          }
                          className="w-24 text-right"
                          placeholder="kg DM"
                        />

                      </div>

                    </div>

                  </div>
                );
              }
            )}

          </div>


          <div className="mt-5 rounded-xl border border-primary/15 bg-primary/[0.03] p-4">

            <div className="grid grid-cols-2 gap-3">

              <div>

                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  Total DMI
                </p>

                <p className="mt-1 font-display text-xl font-extrabold text-[hsl(148,38%,16%)]">
                  {numberText(
                    ration?.total_dmi,
                    1
                  )}{" "}
                  <span className="text-xs font-semibold text-muted-foreground">
                    kg/head/day
                  </span>
                </p>

              </div>

              <div>

                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  Feed cost
                </p>

                <p className="mt-1 font-display text-xl font-extrabold text-[hsl(148,38%,16%)]">
                  {ration
                    ? formatCurrency(
                        ration.cost_per_cow_day,
                        currency,
                        2
                      )
                    : "—"}
                </p>

                <p className="text-[10px] text-muted-foreground">
                  /head/day
                </p>

              </div>

            </div>

            <div className="mt-3 border-t pt-3">

              <p className="text-xs text-muted-foreground">
                {selectedFeedCount} feed
                {selectedFeedCount ===
                1
                  ? ""
                  : "s"}{" "}
                included in the ration.
              </p>

            </div>

          </div>


          <div className="mt-5 space-y-3">

            <div>

              <p className="mb-1.5 text-xs font-semibold text-muted-foreground">
                Diet name
              </p>

              <Input
                value={
                  name
                }
                data-testid="diet-name-input"
                onChange={(
                  event
                ) =>
                  setName(
                    event.target
                      .value
                  )
                }
                placeholder="Diet name"
              />

            </div>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">

              <Button
                onClick={() =>
                  saveVersion(
                    false
                  )
                }
                variant="outline"
                className="rounded-full"
                data-testid="save-version-button"
              >
                <Save className="mr-2 h-4 w-4" />
                Save version
              </Button>

              <Button
                onClick={() => {
                  if (
                    !items.length
                  ) {
                    toast.error(
                      "Add at least one feed"
                    );

                    return;
                  }

                  setSaveActiveOpen(
                    true
                  );
                }}
                className="rounded-full"
                data-testid="save-assign-button"
              >
                <Star className="mr-2 h-4 w-4" />
                Save & set active
              </Button>

            </div>

          </div>

        </Card>


        {/* ------------------------------------------------ */}
        {/* Live summary / professional analysis             */}
        {/* ------------------------------------------------ */}

        <div className="xl:col-span-3">

          {!balance ? (

            <Card className="p-10 text-center">

              <Scale className="mx-auto h-7 w-7 text-muted-foreground" />

              <p className="mt-3 font-semibold text-[hsl(148,38%,16%)]">
                Build a ration to begin
              </p>

              <p className="mt-1 text-sm text-muted-foreground">
                Select a group and add feeds to see live nutrition results.
              </p>

            </Card>

          ) : view ===
            "simple" ? (

            <div className="space-y-4">

              <Card className="p-5">

                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">

                  <div>

                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                      Ration summary — live
                    </p>

                    <div className="mt-2 flex flex-wrap items-center gap-3">

                      <p className="font-display text-xl font-bold text-[hsl(148,38%,16%)]">
                        Overall
                      </p>

                      <StatusPill
                        status={
                          overall
                        }
                      />

                      <span className="text-xs font-semibold text-muted-foreground">
                        {overallReason}
                      </span>

                    </div>

                  </div>

                  <Button
                    variant="outline"
                    className="rounded-full"
                    onClick={() =>
                      setView(
                        "professional"
                      )
                    }
                  >
                    View full analysis
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>

                </div>


                <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4">

                  <SummaryTile
                    label="Energy"
                    value={ratioPct(
                      balance
                        .balances
                        .energy_nasem
                        .pct
                    )}
                    unit="of need"
                    status={
                      eStatus
                    }
                  />

                  <SummaryTile
                    label="Protein"
                    value={ratioPct(
                      balance
                        .balances
                        .mp
                        .pct
                    )}
                    unit="of need"
                    status={
                      pStatus
                    }
                  />

                  <SummaryTile
                    label="NDF"
                    value={numberText(
                      ration.ndf_pct,
                      1
                    )}
                    unit="% DM"
                    status={
                      fStatus
                    }
                  />

                  <SummaryTile
                    label="Starch"
                    value={numberText(
                      ration.starch_pct,
                      1
                    )}
                    unit="% DM"
                    status={
                      stStatus
                    }
                  />

                  <SummaryTile
                    label="DMI"
                    value={numberText(
                      ration.total_dmi,
                      1
                    )}
                    unit="kg/day"
                  />

                  <SummaryTile
                    label="Feed cost"
                    value={formatCurrency(
                      ration.cost_per_cow_day,
                      currency,
                      2
                    )}
                    unit="/head/day"
                  />

                  <SummaryTile
                    label="Minerals"
                    value={
                      mineralProblem
                        ? "Review"
                        : "Adequate"
                    }
                    status={
                      mineralProblem
                        ? "review"
                        : "adequate"
                    }
                  />

                  <SummaryTile
                    label="DCAD"
                    value={numberText(
                      balance.dcad
                        ?.value,
                      0
                    )}
                    unit="mEq/kg DM"
                    status={
                      dcadStatus
                    }
                  />

                </div>


                <div
                  className="mt-5 rounded-xl border bg-secondary/20 p-4"
                  data-testid="simple-recommendations"
                >

                  <div className="flex items-center gap-2">

                    <Sparkles className="h-4 w-4 text-primary" />

                    <h4 className="font-display font-bold text-[hsl(148,38%,16%)]">
                      Recommendations
                    </h4>

                  </div>

                  <ul className="mt-3 space-y-2">

                    {(showAllRecommendations
                      ? simpleRecs
                      : simpleRecs.slice(0, 4)
                    ).map(
                      (
                        recommendation,
                        index
                      ) => (
                        <li
                          key={
                            index
                          }
                          className="flex items-start gap-2 text-sm text-[hsl(148,38%,16%)]"
                        >

                          <ArrowRight className="mt-1 h-3.5 w-3.5 shrink-0 text-primary" />

                          {recommendation}

                        </li>
                      )
                    )}

                  </ul>

                  {simpleRecs.length > 4 && (
                    <button
                      type="button"
                      className="mt-3 text-xs font-semibold text-primary hover:underline"
                      onClick={() =>
                        setShowAllRecommendations(
                          (current) => !current
                        )
                      }
                    >
                      {showAllRecommendations
                        ? "Show fewer recommendations"
                        : `Show all ${simpleRecs.length} recommendations`}
                    </button>
                  )}

                  <Collapsible className="mt-4">

                    <CollapsibleTrigger asChild>

                      <button
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary"
                        data-testid="simple-expand-method"
                      >
                        <BookOpen className="h-3.5 w-3.5" />
                        Show method
                        <ChevronDown className="h-3.5 w-3.5" />
                      </button>

                    </CollapsibleTrigger>

                    <CollapsibleContent className="mt-2 rounded-lg border border-dashed p-3 text-xs text-muted-foreground">
                      Energy and protein percentages come from the existing NASEM 2021 requirement balance. Fibre, starch and other ration values come from the ration composition. Switch to Professional for the detailed nutrient balances, equations and references.
                    </CollapsibleContent>

                  </Collapsible>

                </div>

              </Card>

            </div>

          ) : (

            <div
              className="space-y-4"
              data-testid="professional-view"
            >

              <Card className="overflow-x-auto" data-testid="pro-balance-table">

                <div className="border-b border-border p-4">

                  <h3 className="font-display font-bold text-[hsl(148,38%,16%)]">
                    Requirement vs Supply vs Balance
                  </h3>

                  <p className="text-xs text-muted-foreground">
                    {result.model}
                  </p>

                </div>

                <Table>

                  <TableHeader>

                    <TableRow>

                      <TableHead>
                        Nutrient
                      </TableHead>

                      <TableHead className="text-right">
                        Requirement
                      </TableHead>

                      <TableHead className="text-right">
                        Supply
                      </TableHead>

                      <TableHead className="text-right">
                        Balance
                      </TableHead>

                      <TableHead className="text-right">
                        Status
                      </TableHead>

                    </TableRow>

                  </TableHeader>

                  <TableBody>

                    <BalanceRow
                      label="Energy — NASEM 2021"
                      unit="MJ ME"
                      data={
                        balance
                          .balances
                          .energy_nasem
                      }
                    />

                    <BalanceRow
                      label="Energy — Feed into Milk"
                      unit="MJ ME"
                      data={
                        balance
                          .balances
                          .energy_fim
                      }
                    />

                    <BalanceRow
                      label="Metabolizable Protein"
                      unit="g"
                      data={
                        balance
                          .balances
                          .mp
                      }
                    />

                    <BalanceRow
                      label="Crude Protein"
                      unit="g"
                      data={
                        balance
                          .balances
                          .cp
                      }
                    />

                  </TableBody>

                </Table>

              </Card>


              <DcadPanel
                dcad={
                  balance.dcad
                }
              />


              <div className="grid gap-4 xl:grid-cols-2">

                <MineralTable
                  title="Macro Mineral Balance"
                  rows={
                    MACRO_MINERALS
                  }
                  balances={
                    balance
                      .balances
                      ?.minerals
                  }
                />

                <MineralTable
                  title="Trace Mineral Balance"
                  rows={
                    TRACE_MINERALS
                  }
                  balances={
                    balance
                      .balances
                      ?.minerals
                  }
                />

              </div>


              <div className="grid gap-4 md:grid-cols-2">

                <Card className="p-5">

                  <h4 className="mb-2 font-display font-bold text-[hsl(148,38%,16%)]">
                    Energy breakdown (MJ ME)
                  </h4>

                  <Table>

                    <TableHeader>

                      <TableRow>

                        <TableHead>
                          Component
                        </TableHead>

                        <TableHead className="text-right">
                          NASEM
                        </TableHead>

                        <TableHead className="text-right">
                          FiM
                        </TableHead>

                      </TableRow>

                    </TableHeader>

                    <TableBody>

                      {[
                        "maintenance",
                        "activity",
                        "milk",
                        "reserves",
                        "pregnancy",
                        "total",
                      ].map(
                        (
                          key
                        ) => (
                          <TableRow
                            key={
                              key
                            }
                            className={
                              key ===
                              "total"
                                ? "bg-secondary/40 font-bold"
                                : ""
                            }
                          >

                            <TableCell className="capitalize">
                              {key}
                            </TableCell>

                            <TableCell className="text-right tabular-nums">
                              {balance
                                .energy_nasem[
                                  key
                                ]}
                            </TableCell>

                            <TableCell className="text-right tabular-nums">
                              {balance
                                .energy_fim[
                                  key
                                ]}
                            </TableCell>

                          </TableRow>
                        )
                      )}

                    </TableBody>

                  </Table>

                </Card>


                <Card className="p-5">

                  <h4 className="mb-2 font-display font-bold text-[hsl(148,38%,16%)]">
                    MP breakdown (g/day)
                  </h4>

                  <Table>

                    <TableHeader>

                      <TableRow>

                        <TableHead>
                          Item
                        </TableHead>

                        <TableHead className="text-right">
                          Value
                        </TableHead>

                      </TableRow>

                    </TableHeader>

                    <TableBody>

                      {[
                        "maintenance",
                        "milk",
                        "pregnancy",
                        "growth",
                        "total",
                      ].map(
                        (
                          key
                        ) => (
                          <TableRow
                            key={
                              key
                            }
                            className={
                              key ===
                              "total"
                                ? "bg-secondary/40 font-bold"
                                : ""
                            }
                          >

                            <TableCell className="capitalize">
                              Requirement —{" "}
                              {key}
                            </TableCell>

                            <TableCell className="text-right tabular-nums">
                              {balance
                                .protein_req[
                                  key
                                ]}
                            </TableCell>

                          </TableRow>
                        )
                      )}

                      <TableRow>

                        <TableCell>
                          Supply — microbial
                        </TableCell>

                        <TableCell className="text-right tabular-nums">
                          {balance
                            .protein_supply_detail
                            .mp_microbial}
                        </TableCell>

                      </TableRow>

                      <TableRow>

                        <TableCell>
                          Supply — UDP
                        </TableCell>

                        <TableCell className="text-right tabular-nums">
                          {balance
                            .protein_supply_detail
                            .mp_udp}
                        </TableCell>

                      </TableRow>

                      <TableRow className="bg-secondary/40 font-bold">

                        <TableCell>
                          Supply — total
                        </TableCell>

                        <TableCell className="text-right tabular-nums">
                          {balance
                            .protein_supply_detail
                            .mp_total}
                        </TableCell>

                      </TableRow>

                    </TableBody>

                  </Table>

                </Card>

              </div>


              <Card
                className="overflow-x-auto p-5"
                data-testid="pro-feed-contribution"
              >

                <h4 className="mb-2 font-display font-bold text-[hsl(148,38%,16%)]">
                  Feed-by-feed contribution
                </h4>

                <Table>

                  <TableHeader>

                    <TableRow>

                      <TableHead>
                        Feed
                      </TableHead>

                      <TableHead className="text-right">
                        kg DM
                      </TableHead>

                      <TableHead className="text-right">
                        ME MJ
                      </TableHead>

                      <TableHead className="text-right">
                        CP kg
                      </TableHead>

                      <TableHead className="text-right">
                        Ca g
                      </TableHead>

                      <TableHead className="text-right">
                        Mg g
                      </TableHead>

                      <TableHead className="text-right">
                        Na g
                      </TableHead>

                      <TableHead className="text-right">
                        Cost
                      </TableHead>

                    </TableRow>

                  </TableHeader>

                  <TableBody>

                    {(ration.breakdown ||
                      []).map(
                      (
                        item
                      ) => (
                        <TableRow
                          key={
                            item.feed_id
                          }
                        >

                          <TableCell>
                            {item.name}
                          </TableCell>

                          <TableCell className="text-right tabular-nums">
                            {numberText(
                              item.inclusion_kg_dm,
                              2
                            )}
                          </TableCell>

                          <TableCell className="text-right tabular-nums">
                            {numberText(
                              item.me_mj
                            )}
                          </TableCell>

                          <TableCell className="text-right tabular-nums">
                            {numberText(
                              item.cp_kg,
                              2
                            )}
                          </TableCell>

                          <TableCell className="text-right tabular-nums">
                            {numberText(
                              item.ca_g
                            )}
                          </TableCell>

                          <TableCell className="text-right tabular-nums">
                            {numberText(
                              item.mg_g
                            )}
                          </TableCell>

                          <TableCell className="text-right tabular-nums">
                            {numberText(
                              item.na_g
                            )}
                          </TableCell>

                          <TableCell className="text-right tabular-nums">
                            {formatCurrency(
                              item.cost,
                              currency,
                              3
                            )}
                          </TableCell>

                        </TableRow>
                      )
                    )}

                    <TableRow className="bg-secondary/40 font-bold">

                      <TableCell>
                        Diet
                      </TableCell>

                      <TableCell className="text-right">
                        {ration.total_dmi}
                      </TableCell>

                      <TableCell className="text-right">
                        {ration.me_supplied}
                      </TableCell>

                      <TableCell className="text-right">
                        {ration.cp_kg}
                      </TableCell>

                      <TableCell className="text-right">
                        {numberText(
                          ration
                            .macro_minerals_g
                            ?.ca
                        )}
                      </TableCell>

                      <TableCell className="text-right">
                        {numberText(
                          ration
                            .macro_minerals_g
                            ?.mg
                        )}
                      </TableCell>

                      <TableCell className="text-right">
                        {numberText(
                          ration
                            .macro_minerals_g
                            ?.na
                        )}
                      </TableCell>

                      <TableCell className="text-right">
                        {formatCurrency(
                          ration.cost_per_cow_day,
                          currency,
                          2
                        )}
                      </TableCell>

                    </TableRow>

                  </TableBody>

                </Table>

              </Card>


              <Card
                className="p-5"
                data-testid="pro-references"
              >

                <div className="mb-2 flex items-center gap-2">

                  <BookOpen className="h-4 w-4 text-primary" />

                  <h4 className="font-display font-bold text-[hsl(148,38%,16%)]">
                    Equations & references
                  </h4>

                </div>

                <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">

                  {(balance.references ||
                    []).map(
                    (
                      reference
                    ) => (
                      <li
                        key={
                          reference
                        }
                      >
                        {reference}
                      </li>
                    )
                  )}

                </ul>

              </Card>

            </div>
          )}

        </div>

      </div>


      {/* -------------------------------------------------- */}
      {/* Saved rations / history                            */}
      {/* -------------------------------------------------- */}

      <div className="mt-8">

        <div className="mb-4 flex flex-col gap-3 border-b border-border sm:flex-row sm:items-center">

          <div className="flex items-center gap-1">

            {[
              [
                "saved",
                "Saved rations",
              ],
              [
                "history",
                "History",
              ],
            ].map(
              ([
                key,
                label,
              ]) => (
                <button
                  key={
                    key
                  }
                  data-testid={`tab-${key}`}
                  onClick={() =>
                    setManagementTab(
                      key
                    )
                  }
                  className={`-mb-px border-b-2 px-4 py-2 text-sm font-semibold ${
                    managementTab ===
                    key
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground"
                  }`}
                >
                  {label}
                </button>
              )
            )}

          </div>

          <label className="flex items-center gap-2 text-xs text-muted-foreground sm:ml-auto">

            <Checkbox
              checked={
                showArchived
              }
              data-testid="show-archived-check"
              onCheckedChange={
                setShowArchived
              }
            />

            Show archived

          </label>

        </div>


        <Card
          className="overflow-x-auto"
          data-testid={`${managementTab}-tab`}
        >

          <Table>

            <TableHeader>

              <TableRow>

                <TableHead>
                  Diet
                </TableHead>

                <TableHead>
                  Version
                </TableHead>

                <TableHead>
                  Saved
                </TableHead>

                <TableHead>
                  Status
                </TableHead>

                <TableHead className="text-right">
                  Actions
                </TableHead>

              </TableRow>

            </TableHeader>

            <TableBody>

              {(
                managementTab ===
                "saved"
                  ? activeRations
                  : rations
              ).map(
                (
                  rationItem
                ) => {
                  const isActive =
                    rationItem.id ===
                    group
                      ?.active_ration_id;

                  return (
                    <TableRow
                      key={
                        rationItem.id
                      }
                      data-testid={`ration-row-${rationItem.id}`}
                      className={
                        isActive
                          ? "bg-primary/[0.04]"
                          : ""
                      }
                    >

                      <TableCell className="font-medium text-[hsl(148,38%,16%)]">

                        <div className="flex items-center gap-2">

                          {isActive && (
                            <Star className="h-4 w-4 fill-primary text-primary" />
                          )}

                          <span>
                            {rationItem.name}
                          </span>

                          {rationItem.archived && (
                            <span className="text-[10px] text-muted-foreground">
                              (archived)
                            </span>
                          )}

                        </div>

                      </TableCell>

                      <TableCell className="tabular-nums">
                        v
                        {rationItem.version ??
                          1}
                      </TableCell>

                      <TableCell className="text-sm text-muted-foreground">
                        {fmtDate(
                          rationItem.created_at
                        )}
                      </TableCell>

                      <TableCell>

                        {isActive ? (
                          <span className="rounded-full bg-primary/10 px-2 py-1 text-[10px] font-bold uppercase text-primary">
                            Active
                          </span>
                        ) : (
                          <span className="rounded-full bg-secondary px-2 py-1 text-[10px] uppercase text-muted-foreground">
                            Saved
                          </span>
                        )}

                      </TableCell>

                      <TableCell className="text-right">

                        <div className="inline-flex flex-wrap justify-end gap-1">

                          <Button
                            size="icon"
                            variant="ghost"
                            title="Load/View"
                            data-testid={`view-${rationItem.id}`}
                            onClick={() =>
                              loadItems(
                                rationItem
                              )
                            }
                          >
                            <Eye className="h-4 w-4" />
                          </Button>

                          <Button
                            size="icon"
                            variant="ghost"
                            title="Duplicate"
                            data-testid={`dup-${rationItem.id}`}
                            onClick={() =>
                              duplicate(
                                rationItem
                              )
                            }
                          >
                            <Copy className="h-4 w-4" />
                          </Button>

                          <Button
                            size="icon"
                            variant="ghost"
                            title="Rename"
                            data-testid={`rename-${rationItem.id}`}
                            onClick={() =>
                              rename(
                                rationItem
                              )
                            }
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>

                          <Button
                            size="icon"
                            variant="ghost"
                            title="Compare with active"
                            data-testid={`compare-${rationItem.id}`}
                            onClick={() =>
                              setCompareId(
                                rationItem.id
                              )
                            }
                          >
                            <GitCompare className="h-4 w-4" />
                          </Button>

                          {managementTab ===
                            "history" && (
                            <Button
                              size="icon"
                              variant="ghost"
                              title="Restore as new version"
                              data-testid={`restore-${rationItem.id}`}
                              onClick={() =>
                                restore(
                                  rationItem
                                )
                              }
                            >
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                          )}

                          {!isActive && (
                            <Button
                              size="icon"
                              variant="ghost"
                              title="Set as active diet"
                              data-testid={`assign-${rationItem.id}`}
                              onClick={() =>
                                setAssignTarget(
                                  rationItem.id
                                )
                              }
                            >
                              <Star className="h-4 w-4" />
                            </Button>
                          )}

                          <Button
                            size="icon"
                            variant="ghost"
                            title={
                              rationItem.archived
                                ? "Unarchive"
                                : "Archive"
                            }
                            data-testid={`archive-${rationItem.id}`}
                            onClick={() =>
                              archive(
                                rationItem
                              )
                            }
                          >
                            <Archive className="h-4 w-4" />
                          </Button>

                        </div>

                      </TableCell>

                    </TableRow>
                  );
                }
              )}

              {rations.length ===
                0 && (
                <TableRow>

                  <TableCell
                    colSpan={
                      5
                    }
                    className="py-8 text-center text-muted-foreground"
                  >
                    No saved rations yet.
                  </TableCell>

                </TableRow>
              )}

            </TableBody>

          </Table>

        </Card>


        {/* ------------------------------------------------ */}
        {/* Compare                                         */}
        {/* ------------------------------------------------ */}

        {compareRation && (

          <Card
            className="mt-4 p-5"
            data-testid="compare-panel"
          >

            <div className="mb-3 flex items-center justify-between">

              <h4 className="flex items-center gap-2 font-display font-bold text-[hsl(148,38%,16%)]">

                <GitCompare className="h-4 w-4 text-primary" />

                Compare vs active ration

              </h4>

              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  setCompareId(
                    null
                  )
                }
              >
                Close
              </Button>

            </div>

            <Table>

              <TableHeader>

                <TableRow>

                  <TableHead>
                    Metric
                  </TableHead>

                  <TableHead className="text-right">
                    {compareRation.name} (v
                    {compareRation.version})
                  </TableHead>

                  <TableHead className="text-right">
                    Active
                    {activeRation
                      ? ` (${activeRation.name})`
                      : ""}
                  </TableHead>

                </TableRow>

              </TableHeader>

              <TableBody>

                {[
                  [
                    "ME density",
                    "me_density",
                    " MJ/kg",
                  ],
                  [
                    "NEL density",
                    "nel_density",
                    " MJ/kg",
                  ],
                  [
                    "CP",
                    "cp_pct",
                    "%",
                  ],
                  [
                    "NDF",
                    "ndf_pct",
                    "%",
                  ],
                  [
                    "peNDF",
                    "pendf_pct",
                    "%",
                  ],
                  [
                    "Starch",
                    "starch_pct",
                    "%",
                  ],
                  [
                    "Ether extract",
                    "ee_pct",
                    "%",
                  ],
                  [
                    "DCAD",
                    "dcad.value",
                    " mEq/kg DM",
                  ],
                  [
                    "Cost/head/day",
                    "cost_per_cow_day",
                    "",
                  ],
                ].map(
                  ([
                    label,
                    key,
                    unit,
                  ]) => {
                    const read =
                      (
                        object,
                        path
                      ) =>
                        path
                          .split(
                            "."
                          )
                          .reduce(
                            (
                              value,
                              part
                            ) =>
                              value?.[
                                part
                              ],
                            object
                          );

                    const compareValue =
                      read(
                        compareRation.result,
                        key
                      );

                    const activeValue =
                      read(
                        activeRation
                          ?.result,
                        key
                      );

                    return (
                      <TableRow
                        key={
                          key
                        }
                      >

                        <TableCell>
                          {label}
                        </TableCell>

                        <TableCell className="text-right tabular-nums">

                          {key ===
                          "cost_per_cow_day"
                            ? formatCurrency(
                                compareValue,
                                currency,
                                2
                              )
                            : `${
                                compareValue ??
                                "—"
                              }${unit}`}

                        </TableCell>

                        <TableCell className="text-right tabular-nums">

                          {key ===
                          "cost_per_cow_day"
                            ? formatCurrency(
                                activeValue,
                                currency,
                                2
                              )
                            : `${
                                activeValue ??
                                "—"
                              }${unit}`}

                        </TableCell>

                      </TableRow>
                    );
                  }
                )}

              </TableBody>

            </Table>

          </Card>
        )}

      </div>


      {/* -------------------------------------------------- */}
      {/* Confirmation dialogs                               */}
      {/* -------------------------------------------------- */}

      <AlertDialog
        open={
          Boolean(
            assignTarget
          )
        }
        onOpenChange={(
          open
        ) =>
          !open &&
          setAssignTarget(
            null
          )
        }
      >

        <AlertDialogContent data-testid="assign-confirm-dialog">

          <AlertDialogHeader>

            <AlertDialogTitle>
              Set as active ration?
            </AlertDialogTitle>

            <AlertDialogDescription>
              This will change the active ration for{" "}
              <strong>
                {group?.name}
              </strong>
              . The current active ration remains in history.
            </AlertDialogDescription>

          </AlertDialogHeader>

          <AlertDialogFooter>

            <AlertDialogCancel>
              Cancel
            </AlertDialogCancel>

            <AlertDialogAction
              data-testid="assign-confirm-btn"
              onClick={
                confirmAssign
              }
            >
              Set active
            </AlertDialogAction>

          </AlertDialogFooter>

        </AlertDialogContent>

      </AlertDialog>


      <AlertDialog
        open={
          saveActiveOpen
        }
        onOpenChange={
          setSaveActiveOpen
        }
      >

        <AlertDialogContent data-testid="save-active-confirm-dialog">

          <AlertDialogHeader>

            <AlertDialogTitle>
              Save & set as active ration?
            </AlertDialogTitle>

            <AlertDialogDescription>
              This saves a new immutable version and makes it the active ration for{" "}
              <strong>
                {group?.name}
              </strong>
              . The current active ration remains in history.
            </AlertDialogDescription>

          </AlertDialogHeader>

          <AlertDialogFooter>

            <AlertDialogCancel>
              Cancel
            </AlertDialogCancel>

            <AlertDialogAction
              data-testid="save-active-confirm-btn"
              onClick={() => {
                setSaveActiveOpen(
                  false
                );

                saveVersion(
                  true
                );
              }}
            >
              Save & set active
            </AlertDialogAction>

          </AlertDialogFooter>

        </AlertDialogContent>

      </AlertDialog>

    </div>
  );
}
